import React, { useEffect, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material"
import { styles } from "./styles/WordSearchSettings.styles"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { useTranslation } from "react-i18next"
import Image from "components/Image"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import useSound from "hooks/useSound"
import { select } from "utils/sounds"
import { DIRECTIONS } from "../helpers/contants"
import Proptypes from "prop-types"
import { useAuthContext } from "contexts/AuthContext"

WordSearchSettings.propTypes = {
  open: Proptypes.bool.isRequired,
  onClose: Proptypes.func.isRequired,
  onSuccess: Proptypes.func.isRequired,
  easyLevelAvailable: Proptypes.bool,
}

export default function WordSearchSettings({
  open,
  onClose,
  onSuccess,
  easyLevelAvailable = true,
  fontSettings = {},
}) {
  const { t } = useTranslation()
  const { audioSettings } = useAuthContext()
  const [playSelect] = useSound(select, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [state, setState] = useState({
    horizontal: true,
    vertical: true,
    diagonal: true,
    reverse_horizontal: false,
    reverse_vertical: false,
    reverse_diagonal: false,
  })
  const [difficulty, setDifficulty] = useState(0)

  useEffect(() => {
    if (!easyLevelAvailable) {
      setDifficulty(1)
    }
    return () => {
      setState({
        horizontal: true,
        vertical: true,
        diagonal: true,
        reverse_horizontal: false,
        reverse_vertical: false,
        reverse_diagonal: false,
      })
      setDifficulty(0)
    }
  }, [open])

  const handleChange = (event) => {
    playSelect()
    setState({ ...state, [event.target.name]: event.target.checked })
  }

  const onChangeValue = React.useCallback(
    (event) => {
      playSelect()
      setDifficulty(parseInt(event.target.value))
    },
    [playSelect]
  )

  const onAccept = () => {
    // return state with selected directions
    const directions = []
    Object.keys(state).forEach((key) => {
      if (state[key]) {
        directions.push(key)
      }
    })
    onSuccess({
      directions,
      difficulty,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={"md"}
      PaperProps={{
        sx: [
          styles.contanier,
          fontSettings?.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
          fontSettings?.bgColor && {
            backgroundColor: fontSettings.bgColor,
          },
        ],
      }}
    >
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <ArrowBackIcon fontSize="large" color="primary" />
      </IconButton>
      <Image name="gameWordSearchActive" sx={styles.mainImage} />
      <DialogTitle textAlign={"center"} mt={1} sx={styles.dialogHeader}>
        {t("word_search")}
      </DialogTitle>
      <DialogContent>
        <Stack
          justifyContent={"center"}
          alignItems={"flex-start"}
          direction={"row"}
          spacing={2}
          minHeight={250}
          // bgcolor={"pink"}
        >
          <Accordion
            sx={styles.optionsContainer}
            defaultExpanded
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="directions-content"
              id="directions-header"
              sx={styles.optionsContent}
            >
              <Typography sx={styles.title}>{t("Directions")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl
                component={"fieldset"}
                variant="standard"
                sx={{ minHeight: 150 }}
                error={
                  Array.from(Object.values(state)).filter((value) => value)
                    .length < 2
                }
              >
                <FormGroup
                  sx={{
                    maxHeight: 150,
                  }}
                >
                  {DIRECTIONS.map((value, index) => (
                    <FormControlLabel
                      key={index}
                      sx={styles.title}
                      control={
                        <Checkbox
                          checked={state[value]}
                          onChange={handleChange}
                          name={value}
                        />
                      }
                      label={t(value)}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>
          <Accordion
            sx={styles.optionsContainer}
            defaultExpanded
            disableGutters
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="difficulty-content"
              id="difficulty-header"
              sx={styles.optionsContent}
            >
              <Typography sx={styles.title}>{t("difficulty")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControl sx={{ minHeight: 150 }}>
                <RadioGroup
                  aria-labelledby="difficulty-buttons-group"
                  name="difficulty-buttons-group"
                  value={difficulty}
                  onChange={onChangeValue}
                >
                  {easyLevelAvailable && (
                    <FormControlLabel
                      sx={styles.title}
                      value={0}
                      control={<Radio />}
                      label={t("easy")}
                    />
                  )}
                  <FormControlLabel
                    sx={styles.title}
                    value={1}
                    control={<Radio />}
                    label={t("intermediate")}
                  />
                </RadioGroup>
              </FormControl>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button
          color="success"
          variant="contained"
          disableElevation
          onClick={() => {
            onAccept()
            onClose()
          }}
          disabled={
            Array.from(Object.values(state)).filter((value) => value).length < 2
          }
        >
          {t("ok")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
